import { useParams, Navigate, useNavigate } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { useAuthContext } from "@/contexts/AuthContext"
import {
  useTournamentInfo,
  useTournamentOrganizers,
  useTournamentPlayers,
} from "@/graphql/hooks/useTournamentDetail"
import { useRoundStart } from "@/graphql/hooks/useRoundStart"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { AlertCircle, Play, FileText } from "lucide-react"
import { PlayerManagementTable } from "@/components/PlayerManagementTable"
import { PlayerSearchForm } from "@/components/PlayerSearchForm"
import { OrganizerSearchForm } from "@/components/OrganizerSearchForm"
import { OrganizerManagementTable } from "@/components/OrganizerManagementTable"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"
import { useToast } from "@/hooks/use-toast"
import { Link } from "react-router-dom"
import { useState } from "react"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tournament } from "@/graphql/types"

const TournamentPlayers = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>()
  const navigate = useNavigate()
  const { userInfo, loading: authLoading, isAuthenticated, isAdmin } = useAuthContext()

  const {
    tournament,
    loading: tournamentLoading,
    error,
    refetch: refetchTournament,
  } = useTournamentInfo(Number(tournamentId))
  const {
    players,
    loading: loadingPlayers,
    refetch: refetchPlayers,
  } = useTournamentPlayers(Number(tournamentId))
  const {
    organizers,
    loading: loadingOrganizers,
    refetch: refetchOrganizers,
  } = useTournamentOrganizers(Number(tournamentId), isAuthenticated)
  const { roundStart, loading: startLoading } = useRoundStart()
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState("players")

  // Check authentication
  if (!authLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check authorization
  const canAccess = () => {
    if (authLoading || !userInfo || !tournament) return false

    // Check if user is admin (we'll assume admins have categoryId 1 for now)
    if (isAdmin) {
      return true
    }

    // Check if user owns the store that owns this tournament
    const isStoreOwner = userInfo.User?.Person?.Store?.id === tournament.storeId

    const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)

    return isAdmin || isStoreOwner || isOrganizer
  }

  const canStartRound = () => {
    if (!tournament || !players || !tournament.TournamentType) {
      return false
    }
    const totalPlayers = players.length
    const minPlayers = tournament.TournamentType.minPlayers || 0
    return totalPlayers >= minPlayers
  }

  const handleStartRound = async () => {
    if (!tournament) return

    try {
      const resolve = await roundStart({ tournamentId: tournament.id })
      // refetch();
      if (resolve.status.success) {
        toast({ title: "Ronda iniciada correctamente", variant: "success" })
        refetchTournament()
        navigate(`${resolve.Round?.urlView}/${tournament.id}`)
      } else {
        toast({
          title: "Error al iniciar ronda",
          description: resolve.status.detail,
          variant: "destructive",
        })
      }
    } catch {
      toast({
        title: "Error al iniciar ronda",
        description: "No se pudo iniciar la ronda del torneo",
        variant: "destructive",
      })
    }
  }

  if (authLoading || tournamentLoading || loadingPlayers || loadingOrganizers) {
    return (
      <Layout>
        <LoadingScreen message="Verificando permisos..." />
      </Layout>
    )
  }

  if (error || !tournament) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Card>
            <CardContent className="p-8 text-center">
              <h2 className="text-2xl font-bold text-muted-foreground mb-4">
                Torneo no encontrado
              </h2>
              <p className="text-muted-foreground">
                No se pudo cargar la información del torneo solicitado.
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  if (!canAccess()) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              No tienes permisos para acceder a la gestión de jugadores de este torneo. Solo los
              administradores y dueños de la tienda organizadora pueden acceder.
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    )
  }

  const tabOptions = [
    { value: "players", label: "Jugadores Inscritos", disabled: false },
    {
      value: "search",
      label: "Buscar Jugador",
      disabled: ![1, 2, 3].includes(tournament.statusId),
    },
    //    {
    //      value: "recurring",
    //      label: "Jugadores Recurrentes",
    //      disabled: ![1, 2, 3].includes(tournament.statusId),
    //    },
    { value: "organizers", label: "Organizadores", disabled: false },
    //    {
    //      value: "register",
    //      label: "Registrar Nuevo",
    //      disabled: ![1, 2, 3].includes(tournament.statusId),
    //    },
  ]

  return (
    <Layout>
      <HeaderTournament tournament={tournament} />
      <div className="container mx-auto px-0 py-0">
        <TournamentNavigation
          tournament={tournament}
          tournamentName={tournament.name || ""}
          tournamentStatusId={tournament.statusId}
          currentPage="players"
          showRounds={false}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          {/* Mobile: Select */}
          <div className="md:hidden">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <SelectValue placeholder="Seleccionar opción" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {tabOptions.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value} disabled={tab.disabled}>
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Desktop: Tabs */}
          <TabsList className="hidden md:grid md:grid-cols-3 gap-2 md:gap-4 p-1 bg-gray-100 dark:bg-gray-700 rounded-full">
            {tabOptions.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                disabled={tab.disabled}
                className="rounded-full px-4 py-2 text-sm text-gray-900 dark:text-gray-100
                 data-[state=active]:bg-primary dark:data-[state=active]:bg-blue-600
                 data-[state=active]:text-primary-foreground dark:data-[state=active]:text-white
                 disabled:opacity-50 transition-colors"
              >
                {tab.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="players">
            <Card className="bg-white dark:bg-gray-800 shadow-md rounded-xl">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                  <CardTitle className="text-lg md:text-xl text-gray-900 dark:text-gray-100">
                    Jugadores Inscritos ({players?.length || 0})
                  </CardTitle>

                  <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    {players?.length > 0 && (
                      <Button
                        variant="outline"
                        size="sm"
                        asChild
                        className="flex items-center gap-2 justify-center sm:justify-start"
                      >
                        <Link
                          to={`/tournament/players/print/${tournamentId}`}
                          target="_blank"
                          className="text-gray-900 dark:text-gray-100 hover:underline"
                        >
                          <FileText className="h-4 w-4" />
                          Ver Lista para Imprimir
                        </Link>
                      </Button>
                    )}

                    {canStartRound() && tournament.statusId === 2 && (
                      <Button
                        onClick={handleStartRound}
                        disabled={startLoading}
                        className="flex items-center gap-2 justify-center sm:justify-start"
                      >
                        <Play className="h-4 w-4" />
                        {startLoading ? "Iniciando..." : "Iniciar Ronda"}
                      </Button>
                    )}
                  </div>
                </div>

                {tournament.TournamentType && (
                  <p className="text-sm text-muted-foreground dark:text-gray-400">
                    Mínimo de jugadores requeridos: {tournament.TournamentType.minPlayers}
                  </p>
                )}
              </CardHeader>

              <CardContent className="p-2 dark:bg-gray-500">
                {!loadingPlayers && players.length > 0 ? (
                  <PlayerManagementTable
                    players={players}
                    tournament={
                      {
                        ...tournament,
                        statusId: tournament.statusId || "",
                      } as any
                    }
                    onPlayerUpdate={refetchPlayers}
                    isAdmin={isAdmin}
                  />
                ) : (
                  <p className="text-center text-muted-foreground dark:text-gray-400 py-8">
                    No hay jugadores inscritos aún.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="search">
            <PlayerSearchForm
              tournament={tournament as any}
              onPlayerRegistered={refetchPlayers}
              players={players}
            />
          </TabsContent>

          <TabsContent value="recurring">
            <Card>
              <CardHeader>
                <CardTitle>Jugadores Recurrentes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Lista de jugadores recurrentes en desarrollo...
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="organizers">
            <div className="space-y-6">
              {/* Formulario de búsqueda de organizador */}
              <OrganizerSearchForm
                tournament={tournament}
                onOrganizerRegistered={refetchOrganizers}
                className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />

              {/* Tabla de organizadores asignados */}
              <Card className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                <CardHeader>
                  <CardTitle>Organizadores Asignados ({organizers?.length || 0})</CardTitle>
                </CardHeader>
                <CardContent>
                  {organizers && organizers.length > 0 ? (
                    <OrganizerManagementTable
                      organizers={organizers}
                      onOrganizerUpdate={refetchOrganizers}
                      className="bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                    />
                  ) : (
                    <p className="text-center text-muted-foreground py-8 dark:text-gray-300">
                      No hay organizadores asignados.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="register">
            <Card>
              <CardHeader>
                <CardTitle>Registrar Nuevo Jugador</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Formulario de registro de nuevos jugadores en desarrollo...
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  )
}

export default TournamentPlayers
